<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import VisitsListState from './VisitsListState.vue';
import VisitPopoverMenu from './VisitPopoverMenu.vue';
import VisitsLoadMore from './VisitsLoadMore.vue';

defineProps({
  visitas: { type: Array, default: () => [] },
  selected: { type: Object, required: true },
  allSelected: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  loadError: { type: [String, Object], default: '' },
  visitsLoadingState: { type: Object, default: () => ({}) },
  visitsEmptyState: { type: Object, default: () => ({}) },
  visitTableLabels: { type: Object, default: () => ({}) },
  visitCopy: { type: Object, required: true },
  perPage: { type: Number, default: 10 },
  page: { type: Number, default: 1 },
  totalPages: { type: Number, default: 1 },
  canManageAvailability: { type: Boolean, default: false },
  canReviewVisits: { type: Boolean, default: false },
  canReviewVisitsAction: { type: Boolean, default: false },
  statusClass: { type: Function, required: true },
  statusLabel: { type: Function, required: true },
  isVisitBlocked: { type: Function, required: true },
  visitingBlockedReason: { type: Function, required: true },
  cancellationReason: { type: Function, required: true },
  isVisitExpired: { type: Function, required: true },
  visitTimeExpiredLabel: { type: String, required: true },
  visitRuntimeBadge: { type: Function, required: true },
  runtimeToneClass: { type: Function, required: true },
  listingContext: { type: Function, required: true },
  visitIntention: { type: Function, required: true },
  entryAuthorization: { type: Function, required: true },
  hasVisitorCheckIn: { type: Function, required: true },
  formatDate: { type: Function, required: true },
  formatTime: { type: Function, required: true },
  visitCardView: { type: Function, required: true },
  shouldShowConfirmCTA: { type: Function, required: true },
  canCancelVisitAction: { type: Function, required: true },
  canRescheduleVisitAction: { type: Function, required: true },
  isPoolVisit: { type: Function, required: true },
  canFollowUpVisitAction: { type: Function, required: true },
});

const emit = defineEmits([
  'retry',
  'toggle-all',
  'toggle-one',
  'open-details',
  'open-blocked-sheet',
  'open-confirmation',
  'open-cancel',
  'reschedule',
  'open-quick-actions',
  'open-finish',
  'review',
  'change-per-page',
  'previous-page',
  'next-page',
]);

const perPageOptions = [10, 25, 50];
</script>

<template>
  <div class="hidden overflow-x-auto md:block">
    <table class="min-w-[1080px] w-full table-fixed text-[14px]">
      <thead>
        <tr class="text-left text-lais-text border-b border-lais-border">
          <th class="w-10 py-3.5">
            <input
              type="checkbox"
              :checked="allSelected"
              class="size-4 accent-lais-primary rounded"
              @change="emit('toggle-all')"
            />
          </th>
          <th class="w-[230px] px-3 py-3.5 font-semibold">
            {{ visitTableLabels.address }}
          </th>
          <th class="w-[180px] px-3 py-3.5 font-semibold">
            {{ visitTableLabels.status }}
          </th>
          <th class="w-[80px] px-3 py-3.5 font-semibold">
            {{ visitTableLabels.context }}
          </th>
          <th class="w-[110px] px-3 py-3.5 font-semibold">
            {{ visitCopy.visitType }}
          </th>
          <th class="w-[180px] px-3 py-3.5 font-semibold">
            {{ visitTableLabels.authorization }}
          </th>
          <th class="w-[130px] px-3 py-3.5 font-semibold">
            {{ visitCopy.client }}
          </th>
          <th class="w-[100px] px-3 py-3.5 font-semibold">
            {{ visitCopy.date }}
          </th>
          <th class="w-10" />
        </tr>
      </thead>
      <tbody>
        <VisitsListState
          v-if="visitas.length === 0"
          table
          colspan="9"
          :loading="isLoading"
          :error="loadError"
          :loading-state="visitsLoadingState"
          :empty-state="visitsEmptyState"
          @retry="emit('retry')"
        />
        <template v-else>
          <tr
            v-for="visita in visitas"
            :key="visita.id"
            class="border-b border-lais-border-soft hover:bg-lais-bg-soft cursor-pointer transition-colors"
            @click="emit('open-details', visita.id)"
          >
            <td class="py-3.5" @click.stop>
              <input
                type="checkbox"
                :checked="selected.has(visita.id)"
                class="size-4 accent-lais-primary rounded"
                @change="emit('toggle-one', visita.id)"
              />
            </td>
            <td class="px-3 py-3.5 text-lais-text-body">
              <p class="truncate" :title="visita.imovel?.titulo || '—'">
                {{ visita.imovel?.titulo || '—' }}
              </p>
            </td>
            <td class="px-3 py-3.5">
              <span
                class="inline-block px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide rounded-full"
                :class="statusClass(visita)"
              >
                {{ statusLabel(visita) }}
              </span>
              <p
                v-if="isVisitBlocked(visita)"
                class="mt-1 truncate text-[11px] font-semibold text-orange-700"
                :title="visitingBlockedReason(visita)"
              >
                {{ visitingBlockedReason(visita) }}
              </p>
              <p
                v-else-if="cancellationReason(visita)"
                class="mt-1 truncate text-[11px] text-lais-text-muted"
                :title="cancellationReason(visita)"
              >
                {{ cancellationReason(visita) }}
              </p>
              <p
                v-else-if="isVisitExpired(visita)"
                class="mt-1 text-[11px] font-semibold text-rose-700"
              >
                {{ visitTimeExpiredLabel }}
              </p>
              <p
                v-if="visitRuntimeBadge(visita)"
                class="mt-1 inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                :class="runtimeToneClass(visitRuntimeBadge(visita).tone)"
                :title="visitRuntimeBadge(visita).title"
              >
                <span
                  class="size-3.5"
                  :class="visitRuntimeBadge(visita).icon"
                  aria-hidden="true"
                />
                <span class="truncate">{{
                  visitRuntimeBadge(visita).label
                }}</span>
              </p>
            </td>
            <td class="px-3 py-3.5 text-lais-text-body">
              {{ listingContext(visita) }}
            </td>
            <td class="px-3 py-3.5 text-lais-text-body">
              {{ visitIntention(visita) }}
            </td>
            <td class="px-3 py-3.5 text-lais-text-body">
              <p class="truncate" :title="entryAuthorization(visita)">
                {{ entryAuthorization(visita) }}
              </p>
            </td>
            <td class="px-3 py-3.5 text-lais-text-body">
              <p
                class="flex min-w-0 items-center gap-1 truncate"
                :title="visita.contact?.name || '—'"
              >
                <span class="truncate">{{ visita.contact?.name || '—' }}</span>
                <span
                  v-if="hasVisitorCheckIn(visita)"
                  class="i-lucide-circle-check size-4 shrink-0 text-emerald-700"
                  :title="visitCopy.checkInConfirmed"
                  :aria-label="visitCopy.checkInConfirmed"
                />
              </p>
            </td>
            <td class="px-3 py-3.5">
              <div class="flex flex-col">
                <span class="text-lais-text font-medium">
                  {{ formatDate(visita.scheduledAt) }}
                </span>
                <span class="text-[12px] text-lais-text-muted">
                  {{ formatTime(visita.scheduledAt) }}
                </span>
              </div>
            </td>
            <td class="py-3.5 text-center">
              <div class="flex justify-center gap-1">
                <button
                  v-if="isVisitBlocked(visita) && canManageAvailability"
                  type="button"
                  class="inline-flex items-center justify-center rounded-full border border-orange-200 bg-orange-50 p-2 text-orange-800 transition-colors hover:border-orange-300 hover:bg-orange-100"
                  :aria-label="visitCopy.resolveBlockedVisit"
                  @click.stop="emit('open-blocked-sheet', visita)"
                >
                  <span
                    class="i-lucide-calendar-x-2 size-4"
                    aria-hidden="true"
                  />
                </button>
                <VisitPopoverMenu
                  :visit="visitCardView(visita)"
                  :show-confirm="shouldShowConfirmCTA(visita)"
                  :can-cancel="canCancelVisitAction(visita)"
                  :can-reschedule="canRescheduleVisitAction(visita)"
                  :is-pool="isPoolVisit(visita)"
                  @confirm="emit('open-confirmation', visita)"
                  @cancel="emit('open-cancel', visita)"
                  @cancel-group="emit('open-cancel', visita)"
                  @reschedule="emit('reschedule', visita)"
                  @more-actions="emit('open-quick-actions', visita)"
                />
                <button
                  v-if="canFollowUpVisitAction(visita)"
                  type="button"
                  class="inline-flex items-center justify-center rounded-full border border-transparent p-2 text-lais-text-empty transition-colors hover:border-lais-border hover:text-lais-primary"
                  :aria-label="visitCopy.finishVisit"
                  @click.stop="emit('open-finish', visita)"
                >
                  <span class="i-lucide-flag size-4" aria-hidden="true" />
                </button>
                <button
                  v-if="canReviewVisits"
                  type="button"
                  class="inline-flex items-center justify-center rounded-full border border-transparent p-2 text-lais-text-empty transition-colors hover:border-lais-border hover:text-lais-primary"
                  :disabled="!canReviewVisitsAction"
                  :aria-label="visitCopy.reviewVisit"
                  @click.stop="emit('review', visita.id)"
                >
                  <span
                    class="i-lucide-clipboard-check size-4"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>

  <div
    class="flex flex-col gap-3 border-t border-lais-border bg-white px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10"
  >
    <div
      class="flex flex-wrap items-center gap-2 text-[13px] text-lais-text-body"
    >
      <select
        :value="perPage"
        class="h-8 px-2 border border-lais-border rounded-lais-sm text-lais-text focus:outline-none focus:border-lais-primary"
        @change="emit('change-per-page', Number($event.target.value))"
      >
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
      <span>{{ visitCopy.itemsPerPage }}</span>
    </div>
    <div
      class="flex flex-wrap items-center gap-3 text-[13px] text-lais-text-body"
    >
      <span>{{ visitCopy.pageLabel(page, totalPages) }}</span>
      <VisitsLoadMore
        v-if="page < totalPages"
        :loading="isLoading"
        @click="emit('next-page')"
      />
      <button
        class="size-8 grid place-items-center border border-lais-border rounded-lais-sm text-lais-text-body hover:border-lais-primary hover:text-lais-primary disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="page === 1"
        :aria-label="visitCopy.previousPage"
        @click="emit('previous-page')"
      >
        <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
      </button>
      <button
        class="size-8 grid place-items-center border border-lais-border rounded-lais-sm text-lais-text-body hover:border-lais-primary hover:text-lais-primary disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="page >= totalPages"
        :aria-label="visitCopy.nextPage"
        @click="emit('next-page')"
      >
        <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
